import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DocumentTitle from "@/components/DocumentTitle";
import { useAuth } from "@/components/AuthProvider";
import { INSIDER_UNLOCKS, foundingBannerLine } from "@/lib/levels";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });
const META = "var(--ww-text-muted)";
const LINE = "var(--ww-line)";
const CARD = "var(--ww-surface)";

function bearer() {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * D46 R2 §1 + trial addendum · membership page.
 *  · Public counter of founding seats reserved.
 *  · Sign-in → "start free for 30 days" (no payment).
 *  · Trialing user → shows days left + reminder near end of trial.
 *  · Comped users → banner "founding member (complimentary)".
 *  · Free users out of seats → "trial closed, join the paid tier at KES 500".
 */
export default function MembershipPage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  // Msg 234 §5 · trial sign-up fields.
  const [phone, setPhone] = useState("+254");
  const [payMethod, setPayMethod] = useState("mpesa");
  const [charge, setCharge] = useState(false);

  useEffect(() => {
    client.get("/membership/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!user) return;
    client.get("/membership/me", { headers: bearer() }).then((r) => setMine(r.data)).catch(() => setMine(null));
  }, [user]);

  const phoneClean = phone.replace(/\s|-/g, "");
  const phoneValid = /^\+\d{8,15}$/.test(phoneClean);
  const canStart = phoneValid && !!payMethod && charge;

  const startTrial = async () => {
    if (!user) return nav("/admin/login?next=/app/membership");
    if (!canStart) {
      toast.error(!phoneValid ? "enter a valid phone number"
                    : !charge ? "please tick the charge consent"
                    : "pick a payment method");
      return;
    }
    setBusy(true);
    try {
      const r = await client.post("/membership/join", {
        consent: true,
        phone: phoneClean,
        pay_method: payMethod,
        charge_after_trial: true,
      }, { headers: bearer() });
      setMine((old) => ({ ...(old || {}), membership_state: r.data.membership_state,
                          trial_ends_at: r.data.trial_ends_at,
                          trial_days_left: r.data.trial_days_left, is_paid: true }));
      // Msg 232 · fix free-trial bug: pull the fresh user record so every
      // isPaid check across the app (follow buttons, members' favourites,
      // composers, profile pill) flips on immediately.
      if (refresh) await refresh();
      toast.success("trial started, you're in");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const messages = {
        phone_required: "enter a valid phone number",
        pay_method_required: "pick m-pesa or card",
        charge_consent_required: "please tick the charge consent",
        consent_required: "consent is required",
      };
      toast.error(messages[detail] || "could not start trial");
    } finally { setBusy(false); }
  };

  const seatsTaken = status?.founding_taken ?? 0;
  const cap = status?.founding_cap ?? 1000;
  const remaining = status?.founding_remaining ?? (cap - seatsTaken);
  const seatNumber = seatsTaken + 1;
  const pct = Math.min(100, Math.round((seatsTaken / cap) * 100));

  const state = mine?.membership_state || (user ? "free" : "anonymous");
  const daysLeft = mine?.trial_days_left;

  return (
    <div className="flex h-full flex-col" data-testid="membership-page"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title="join watu wako · membership" />
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-10" style={{ overscrollBehavior: "contain" }}>
        <div className="mx-auto" style={{ maxWidth: 720 }}>
          <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 11, color: META }}>
            founding crew
          </p>
          <h1 className="mt-1 lowercase" data-testid="founding-banner"
              style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 28, lineHeight: 1.14, color: "var(--ww-text)" }}>
            become a founding insider · <span className="ww-currency">KES</span> 200 a month, for life · {remaining.toLocaleString()} of 1,000 seats left · start free for 30 days
          </h1>
          <p className="mt-3" data-testid="membership-seats-counter"
             style={{ fontFamily: "Arial", fontSize: 13, color: META }}>
            seat #{seatNumber} is yours. {remaining.toLocaleString()} left of 1,000.
          </p>

          {/* Counter */}
          <div className="mt-5 rounded-2xl border p-4" data-testid="founding-counter"
               style={{ borderColor: LINE, background: CARD }}>
            <div className="flex items-baseline justify-between">
              <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 24 }}>
                {seatsTaken} / {cap}
              </p>
              <p style={{ fontFamily: "Arial", fontSize: 12, color: META, textTransform: "uppercase", letterSpacing: "0.18em" }}>
                founding seats reserved
              </p>
            </div>
            <div className="mt-2" style={{ height: 8, borderRadius: 999, background: "var(--ww-sunken)", overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: "#FFD60A" }} />
            </div>
          </div>

          {/* State-specific block */}
          <div className="mt-6">
            {state === "trialing" && (
              <div className="rounded-2xl border p-4" data-testid="membership-trial-state"
                   style={{ borderColor: "#FFD60A", background: CARD }}>
                <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 11, color: "#FFD60A" }}>
                  free trial
                </p>
                <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 22, marginTop: 4 }}>
                  insider · {daysLeft ?? "—"} days free left
                </p>
                {daysLeft != null && daysLeft <= 2 && (
                  <p className="mt-2" style={{ fontFamily: "Arial", fontSize: 13, color: META }}>
                    your trial ends soon. we'll turn on payments before then; you'll be first to know.
                  </p>
                )}
                <p className="mt-2" style={{ fontFamily: "Arial", fontSize: 13, color: META }}>
                  you have full paid access: squads, posting, reviews and marking "went".
                </p>
              </div>
            )}
            {state === "comped" && (
              <div className="rounded-2xl border p-4" data-testid="membership-comped-state"
                   style={{ borderColor: LINE, background: CARD }}>
                <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 18 }}>
                  founding crew · complimentary
                </p>
                <p className="mt-1" style={{ fontFamily: "Arial", fontSize: 13, color: META }}>
                  no expiry. thanks for backing the launch.
                </p>
              </div>
            )}
            {(state === "free" || state === "anonymous" || state === "waitlist") && (
              <>
                {/* Msg 234 §5 · trial sign-up fields */}
                <div className="rounded-2xl border p-4" data-testid="trial-signup-fields"
                     style={{ borderColor: LINE, background: CARD, marginBottom: 12 }}>
                  <label className="block" style={{ fontFamily: "Arial", fontSize: 12, color: META, textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 6 }}>
                    phone number
                  </label>
                  <input data-testid="trial-phone-input" type="tel" inputMode="tel"
                         value={phone} onChange={(e) => setPhone(e.target.value)}
                         placeholder="+254712345678"
                         style={{ width: "100%", height: 48, padding: "0 14px",
                                  background: "var(--ww-sunken)", border: `1px solid ${phone && !phoneValid ? "#FF6B6B" : LINE}`,
                                  borderRadius: 10, color: "var(--ww-text)",
                                  fontFamily: "Arial", fontSize: 15, outline: "none" }} />
                  <p style={{ fontFamily: "Arial", fontSize: 11, color: META, marginTop: 4 }}>
                    kept private. we only use it for your m-pesa charge or card verification.
                  </p>

                  <p className="mt-4" style={{ fontFamily: "Arial", fontSize: 12, color: META, textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 6 }}>
                    payment method
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {[["mpesa","m-pesa"],["card","card"]].map(([k, l]) => (
                      <button key={k} type="button"
                              data-testid={`trial-pay-${k}`}
                              onClick={() => setPayMethod(k)}
                              style={{ padding: "10px 16px", borderRadius: 999, minHeight: 44,
                                       background: payMethod === k ? "#FFD60A" : "transparent",
                                       color: payMethod === k ? "#1A1A30" : "var(--ww-text)",
                                       border: `1px solid ${payMethod === k ? "#FFD60A" : "var(--ww-line-strong)"}`,
                                       fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                                       textTransform: "lowercase", cursor: "pointer" }}>
                        {l}
                      </button>
                    ))}
                  </div>

                  <label className="mt-4 flex gap-3" style={{ cursor: "pointer", alignItems: "flex-start" }}>
                    <input type="checkbox" data-testid="trial-charge-consent"
                           checked={charge} onChange={(e) => setCharge(e.target.checked)}
                           style={{ marginTop: 3, width: 18, height: 18, accentColor: "#FFD60A" }} />
                    <span style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text)", lineHeight: 1.5 }}>
                      charge me <span style={{ fontWeight: 700 }}><span className="ww-currency">KES</span> 200</span> a month after my 30 days.
                      i can cancel before then and pay nothing.
                    </span>
                  </label>
                </div>

                <button data-testid="membership-start-trial"
                        onClick={startTrial} disabled={busy || !canStart}
                        style={{ width: "100%", padding: "16px 20px", borderRadius: 14,
                                 background: canStart ? "#FFD60A" : "var(--ww-sunken)",
                                 color: canStart ? "#1A1A30" : META, border: `1px solid ${canStart ? "#FFD60A" : "var(--ww-line-strong)"}`,
                                 fontFamily: "Poppins", fontWeight: 800, fontSize: 16,
                                 textTransform: "lowercase",
                                 cursor: (busy || !canStart) ? "not-allowed" : "pointer",
                                 minHeight: 56 }}>
                  {busy ? "starting…" : "start free for 30 days"}
                </button>
              </>
            )}
            {state === "paid" && (
              <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 18 }}>
                founding crew · thanks for supporting us
              </p>
            )}
          </div>

          {/* Wave C · what insiders unlock (shared list from lib/levels.js). */}
          <section className="mt-8" data-testid="insider-unlocks-section">
            <h2 className="mb-2 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 20 }}>
              what insiders unlock
            </h2>
            <ul style={{ fontFamily: "Arial", fontSize: 15, color: META, lineHeight: 1.7 }}>
              {INSIDER_UNLOCKS.map((line) => (
                <li key={line}>· {line}</li>
              ))}
            </ul>
            <p className="mt-3" style={{ fontFamily: "Arial", fontSize: 13, color: META }}>
              browsing stays free forever · for you, discover, events, venues and curator pages.
            </p>
          </section>

          <p className="mt-6 text-center">
            <Link to="/app" style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 13, color: META, textDecoration: "none" }}>
              ← back to for you
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
